// pages/Dashboard/Finances/FinancialChart.tsx
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../../hooks/useLanguage';
import { TrendingUp } from 'lucide-react';

interface FinancialChartProps {
    data: Array<{
        date: string;
        revenue: number;
        commission: number;
        netEarnings: number;
    }>;
}

export const FinancialChart: React.FC<FinancialChartProps> = ({ data }) => {
    const { t, isRTL, currentLanguage } = useLanguage();

    // Formater les données pour le graphique
    const chartData = data.map(item => ({
        date: new Date(item.date).toLocaleDateString(
            currentLanguage === 'ar' ? 'ar-SA' :
                currentLanguage === 'en' ? 'en-GB' : 'fr-FR',
            { day: '2-digit', month: 'short' }
        ),
        [t('finances.chart.revenue')]: item.revenue,
        [t('finances.chart.commission')]: item.commission,
        [t('finances.chart.netEarnings')]: item.netEarnings
    }));

    if (!data || data.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp size={20} className="text-indigo-600" />
                    {t('finances.chart.title')}
                </h3>
                <div className="text-center py-8 sm:py-12 text-gray-500 text-sm">
                    {t('finances.chart.noData')}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                <TrendingUp size={20} className="text-indigo-600" />
                {t('finances.chart.title')}
            </h3>

            {/* Chart height smaller on mobile */}
            <div className="h-48 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="date"
                            stroke="#6b7280"
                            reversed={isRTL}
                            tick={{ fontSize: 11 }}
                        />
                        <YAxis
                            stroke="#6b7280"
                            orientation={isRTL ? "right" : "left"}
                            tick={{ fontSize: 11 }}
                            width={45}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                            type="monotone"
                            dataKey={t('finances.chart.revenue')}
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey={t('finances.chart.commission')}
                            stroke="#a855f7"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey={t('finances.chart.netEarnings')}
                            stroke="#22c55e"
                            strokeWidth={3}
                            dot={{ r: 5 }}
                            activeDot={{ r: 7 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Summary cards — 1 col on mobile, 3 on sm+ */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 sm:mt-6">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-xs font-medium text-blue-800">
                            {t('finances.chart.revenue')}
                        </span>
                    </div>
                    <p className="text-base sm:text-lg font-bold text-blue-600">
                        {data.reduce((sum, item) => sum + item.revenue, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    </p>
                </div>

                <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <span className="text-xs font-medium text-purple-800">
                            {t('finances.chart.commission')}
                        </span>
                    </div>
                    <p className="text-base sm:text-lg font-bold text-purple-600">
                        {data.reduce((sum, item) => sum + item.commission, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    </p>
                </div>

                <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-xs font-medium text-green-800">
                            {t('finances.chart.netEarnings')}
                        </span>
                    </div>
                    <p className="text-base sm:text-lg font-bold text-green-600">
                        {data.reduce((sum, item) => sum + item.netEarnings, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    </p>
                </div>
            </div>
        </div>
    );
};